import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBotCheckoutHandler } from '../../../components/bot/hooks/useBotCheckoutHandler';

/**
 * Entry point for WhatsApp checkout deep-links.
 *
 * The bot, on WhatsApp, replies with an absolute signed link
 * `…/shopping-cart?bot_session_id=<id>&token=<signed>`. When that page loads we
 * run the SAME web checkout handler the chat CTA uses: it fetches the assembled
 * `ShoppingCartV2` (passing the token for BE verification), writes
 * `localStorage.cart[uuid]` + `cartV2[uuid]`, and navigates to
 * `/checkout/<uuid>` — so WhatsApp and web converge on one V2 checkout.
 *
 * Idempotent per session_id seen in the URL. On failure (e.g. expired token →
 * 403) it stays silent and leaves the user on the current page.
 */
export function useBotSessionHydration(): { isHydrating: boolean } {
  const location = useLocation();
  const goToCheckout = useBotCheckoutHandler();
  const lastHandledRef = useRef<string | null>(null);
  const inFlightRef = useRef<boolean>(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('bot_session_id');
    if (!sessionId || sessionId === lastHandledRef.current) return;
    if (inFlightRef.current) return;

    const token = params.get('token') ?? undefined;
    inFlightRef.current = true;
    lastHandledRef.current = sessionId;

    (async () => {
      try {
        // Builds the V2 cart, stashes it, and navigates to /checkout/<uuid>.
        await goToCheckout(sessionId, token);
      } catch (err) {
        // Stay silent: a failed redirect (e.g. expired link) just leaves the
        // user where they are; the empty-cart UI handles the rest.
        // eslint-disable-next-line no-console
        console.warn('[useBotSessionHydration] checkout redirect failed', err);
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [location.search, goToCheckout]);

  return { isHydrating: inFlightRef.current };
}
